import { averageValidNumbers } from '../shared-util';
import { FitnessPlan } from './fitness-plan';
import { DeclaredStrength } from './user-state';

export enum TrainingLevel {
  Novice = 100,
  Intermediate = 200,
  Advanced = 300,
}

export type TrainingLevelEnumName = 'Novice' | 'Intermediate' | 'Advanced';

export interface TrainingLevels {
  ['$bench_press']: TrainingLevel;
  ['$chin_up']: TrainingLevel;
  ['$ohp']: TrainingLevel;
  ['$rdl']: TrainingLevel;
  ['$squat']: TrainingLevel;
}

export function trainingLevelToFactor(level: TrainingLevel): number {
  return {
    [TrainingLevel.Novice]: 1,
    [TrainingLevel.Intermediate]: 2,
    [TrainingLevel.Advanced]: 3,
  }[level];
}

export function trainingFactorToLevel(factor: number): TrainingLevel {
  return {
    [1]: TrainingLevel.Novice,
    [2]: TrainingLevel.Intermediate,
    [3]: TrainingLevel.Advanced,
  }[factor];
}

export function averageTrainingLevels(levels: TrainingLevel[]): TrainingLevel {
  const average = averageValidNumbers(levels.map(l => trainingLevelToFactor(l)), 0);
  const normalizedAverage = Math.max(1, Math.min(3, Math.round(average)));
  return trainingFactorToLevel(normalizedAverage);
}

/**
 * Get highest training level that at least two slots from the list have.
 * If has only one input, return that.
 */
export function minTwoTrainingLevels(levels: TrainingLevel[]): TrainingLevel {
  if (levels.length <= 0) {
    return TrainingLevel.Novice;
  }
  if (levels.length === 1) {
    return levels[0];
  }

  for (const minLevel of [TrainingLevel.Advanced, TrainingLevel.Intermediate, TrainingLevel.Novice]) {
    if (levels.filter(l => l >= minLevel).length >= 2) {
      return minLevel;
    }
  }
  return TrainingLevel.Novice;
}

export function getTrainingLevels(declaredStrength: DeclaredStrength, fitnessPlan: FitnessPlan, mode?: 'new' | 'change'): TrainingLevels {
  let findSlotLevel = (id: string) => {
    if (mode === 'new') {
      let slot = declaredStrength.slots.find(s => s.idSlot === 'slot/' + id);
      if (slot && slot.idExercise && slot.trainingLevel) {
        return slot.trainingLevel;
      } else {
        return TrainingLevel.Novice;
      }
    } else {
      return (fitnessPlan.trainingLevels && fitnessPlan.trainingLevels[id]) || TrainingLevel.Novice;
    }
  };

  let trainingLevels: TrainingLevels = {
    ['$bench_press']: findSlotLevel('$bench_press'),
    ['$chin_up']: findSlotLevel('$chin_up'),
    ['$ohp']: findSlotLevel('$ohp'),
    ['$rdl']: findSlotLevel('$rdl'),
    ['$squat']: findSlotLevel('$squat'),
  };

  return trainingLevels;
}
